import React from "react";
import { useTranslation } from "react-i18next";
import type { LayerData } from "../utils/brickOptimizer";
import { physicalSize } from "../utils/physicalSize";

interface ModelResultStripProps {
  model: {
    layers: LayerData[];
    width: number;
    height: number;
  };
}

/**
 * The payoff, right under the stage: what the build is (bricks, layers,
 * studs, size) and a way into the instructions, the layout and the bill of
 * materials — so the results are one click away, not three screens down.
 */
const ModelResultStrip: React.FC<ModelResultStripProps> = ({ model }) => {
  const { t } = useTranslation();
  const bricks = model.layers.reduce((sum, layer) => sum + layer.brickCount, 0);
  const size = physicalSize(model.width, model.height);

  const linkClass =
    "rounded-full border-[1.5px] border-ink bg-surface px-3 py-1 text-xs font-semibold text-ink shadow-hard-sm transition duration-150 ease-out-expo motion-reduce:transition-none hover:text-brick hover:border-brick focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick";

  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="spec-chip text-ink tabular-nums">
          {bricks} {t("instructions.bricks")}
        </span>
        <span className="spec-chip text-ink tabular-nums">
          ~{size.widthCm} × {size.heightCm} cm ·{" "}
          {t("converter.baseplate", { count: size.baseplates })}
        </span>
      </div>
      <nav aria-label={t("common.jumpTo")} className="flex flex-wrap items-center gap-2">
        <a href="#section-instructions" className={linkClass}>
          {t("instructions.title")}
        </a>
        <a href="#section-layout" className={linkClass}>
          {t("instructions.legoLayout")}
        </a>
        <a href="#section-bom" className={linkClass}>
          {t("bom.title")}
        </a>
      </nav>
    </div>
  );
};

export default ModelResultStrip;
